import { PRIORITY_RANK, type AttributionTouch } from "./attributionTypes";

/**
 * Resolve attribution from URL params with strict priority ordering:
 * referral/QR > partner > influencer > sportsbook > campaign > tenant.
 * Geo is NEVER used here — it is a compliance-only fallback.
 */
export function resolveTouchFromUrl(search: string): AttributionTouch | null {
  const sp = new URLSearchParams(search);
  const ref = sp.get("ref");
  const qr = sp.get("qr");
  const partner = sp.get("partner") ?? sp.get("p");
  const influencer = sp.get("influencer") ?? sp.get("inf");
  const sportsbook = sp.get("sb") ?? sp.get("sportsbook");
  const campaign = sp.get("utm_campaign") ?? sp.get("campaign") ?? sp.get("c");
  const tenant = sp.get("tenant") ?? sp.get("t");

  if (!ref && !qr && !partner && !influencer && !sportsbook && !campaign && !tenant) {
    return null;
  }

  const base = {
    partnerId: partner ?? null,
    influencerId: influencer ?? null,
    campaignId: campaign ?? null,
    tenantId: tenant ?? null,
    referralCode: ref ?? null,
    qrId: qr ?? null,
    sportsbookPartner: sportsbook ?? null,
    metadata: {
      utm_source: sp.get("utm_source") ?? undefined,
      utm_medium: sp.get("utm_medium") ?? undefined,
      utm_content: sp.get("utm_content") ?? undefined,
    },
  };

  if (ref) return { ...base, source: "referral_code" };
  if (qr) return { ...base, source: "qr" };
  if (partner) return { ...base, source: "partner_link" };
  if (influencer) return { ...base, source: "influencer" };
  if (sportsbook) return { ...base, source: "sportsbook" };
  if (campaign) return { ...base, source: "campaign" };
  return { ...base, source: "tenant" };
}

export function rankFor(touch: AttributionTouch): number {
  return PRIORITY_RANK[touch.source] ?? 90;
}

/** Should a newer touch override an existing attribution? */
export function shouldOverride(
  currentRank: number,
  currentStatus: "active" | "expired" | "transferred",
  incoming: AttributionTouch,
): boolean {
  if (currentStatus !== "active") return true; // expired → transfer
  return rankFor(incoming) < currentRank; // strictly higher priority wins
}